import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CustomerServices } from "../../../core/services/CustomerServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { setSearchString } from "../../../redux/user";
import { CustomerFields, CustomerListParams } from "./CustomerDataTypes";
import "./Customers.css";
import { isEmptyVal } from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import AddressCell from "../../components/Elements/AddressCell";
import PhoneCell from "../../components/Elements/PhoneCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import { displayField } from "../../../utils/helpers";
import {
  roleTypeArr,
  messageTypeArr,
  appConstObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function Campaigns() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CustomerFields[]>([]);
  const headerCompRef = useRef({});
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedCustomer, setSelectedCustomerData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<any>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    smart_search: "",
  };
  const dispatch = useDispatch();

  const [formData, setFormData] =
    useState<CustomerListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const navigate = useNavigate();
  const [failedImages, setFailedImages] = useState<any>([]);

  useEffect(() => {
    if (company.id) {
      headerCompRef.current = company.id;
    } else {
      headerCompRef.current = "";
    }
    if (smartSearchInput.length > 3) {
      setPage(1);
      getCustomers(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  }, [company]);

  async function getCustomers(params: CustomerListParams) {
    try {
      if (smartSearchInput.length < 3) {
        UtilityServices.showToaster({
          message: t("AT_LEAST", {
            field: t("SEARCH").toLowerCase(),
            char: 3,
          }),
          type: messageTypeArr.error,
        });
        return;
      }
      setLoading(true);
      const res = await CustomerServices.getCustomers(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      get("data.total", res) == 0 ? setShowCreate(true) : setShowCreate(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function customerPicModal(value: any) {
    if (
      !failedImages.includes(value.large_image_url) &&
      value.large_image_url != null &&
      value.large_image_url !== ""
    ) {
      setSelectedCustomerData(value);
      modalRef.current?.openModal();
      modalRef.current?.hideDelete();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...{ company: headerCompRef.current },
      ...data,
    };
    let params: CustomerListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getCustomers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function processCreateCustomer() {
    if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
      !company.id
    ) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    dispatch(setSearchString(smartSearchInput));
    navigate(ContentRoutes.CREATE_CAMPAIGN_CUSTOMER);
  }

  const filterCustomers = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCustomers(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    setData([]);
    setTotal(0);
    setShowCreate(false);
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  return (
    <div className="customers">
      <Container className="container">
        <Header className="header mb-2">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item
              colspan={8}
              className="d-flex"
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <h4 className="mb-1">{t("CUSTOMERS")}</h4>
              {showCreate && (
                <Button
                  appearance="primary"
                  className="btn-crt"
                  onClick={processCreateCustomer}
                >
                  {t("CREATE")}
                </Button>
              )}
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Header>
        <Content>
          <div className="row">
            <div className="col-md-6">
              <InputGroup>
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("NAME").toLowerCase() +
                      ", " +
                      t("PHONE").toLowerCase() +
                      ", " +
                      t("OR").toLowerCase() +
                      " " +
                      t("USERNAME").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterCustomers(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterCustomers(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>

          <div
            className={
              total > 0 ? "usr-tbl-border mt-2" : "no-record-table mt-2"
            }
          >
            <Table
              height={200}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column align="center" width={80}>
                <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                <ImageCellFormatter
                  dataKey="large_image_url"
                  rowData={(rowData: any) => rowData}
                  dummyImage={UtilityServices.getLoggedInUser(
                    "user_default_image"
                  )}
                  onFailedImages={getFailedImages}
                  openPictureModal={customerPicModal}
                />
              </Column>
              <Column width={130}>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="name">
                  {(rowData: any) => <span>{displayField(rowData.name)}</span>}
                </CustomCell>
              </Column>

              <Column width={100}>
                <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                <PhoneCell
                  positionFromRight="50%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column flexGrow={1}>
                <CustomHeaderCell align="left">{t("ADDRESS")}</CustomHeaderCell>
                <AddressCell
                  positionFromRight="30%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={selectedCustomer.name}
        heading={selectedCustomer.name}
        body={
          <img
            src={selectedCustomer.large_image_url}
            className="main-profile-pic"
            alt={t("PROFILE_PIC")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
    </div>
  );
}

export default Campaigns;
